import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/**
 * Generates a PDF from selected user cards
 * @param {Array} selectedUsers - Array of selected user objects
 * @param {string} filename - Name for the PDF file (optional)
 */
export const generateUserCardsPDF = async (selectedUsers, filename = 'user-cards.pdf') => {
  if (!selectedUsers || selectedUsers.length === 0) {
    throw new Error('No users selected for PDF generation');
  }

  try {
    // Create a temporary container to hold all selected cards
    const tempContainer = document.createElement('div');
    tempContainer.style.position = 'absolute';
    tempContainer.style.left = '-9999px';
    tempContainer.style.top = '0';
    tempContainer.style.width = '800px'; // Fixed width for consistent PDF layout
    tempContainer.style.backgroundColor = '#ffffff';
    tempContainer.style.padding = '20px';
    document.body.appendChild(tempContainer);

    // Create a title for the PDF
    const title = document.createElement('h1');
    title.textContent = 'User Cards Report';
    title.style.textAlign = 'center';
    title.style.marginBottom = '30px';
    title.style.color = '#333';
    title.style.fontSize = '24px';
    title.style.fontFamily = 'Arial, sans-serif';
    tempContainer.appendChild(title);

    // Create a subtitle with generation info
    const subtitle = document.createElement('p');
    subtitle.textContent = `Generated on ${new Date().toLocaleDateString()} - ${selectedUsers.length} user(s) selected`;
    subtitle.style.textAlign = 'center';
    subtitle.style.marginBottom = '30px';
    subtitle.style.color = '#666';
    subtitle.style.fontSize = '14px';
    subtitle.style.fontFamily = 'Arial, sans-serif';
    tempContainer.appendChild(subtitle);

    // Create cards container
    const cardsContainer = document.createElement('div');
    cardsContainer.style.display = 'grid';
    cardsContainer.style.gridTemplateColumns = 'repeat(2, 1fr)';
    cardsContainer.style.gap = '25px';
    cardsContainer.style.width = '100%';
    tempContainer.appendChild(cardsContainer);

    // Generate HTML for each selected user card
    selectedUsers.forEach((user) => {
      const cardElement = createUserCardElement(user);
      cardsContainer.appendChild(cardElement);
    });

    // Wait a bit for the DOM to render
    await new Promise(resolve => setTimeout(resolve, 100));

    // Convert to canvas
    const canvas = await html2canvas(tempContainer, {
      scale: 2, // Higher quality
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: 800,
      height: tempContainer.scrollHeight
    });

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF('p', 'mm', 'a4');
    
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 295; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;

    let position = 0;

    // Add first page
    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;

    // Add additional pages if needed
    while (heightLeft >= 0) {
      position = heightLeft - imgHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Clean up
    document.body.removeChild(tempContainer);

    // Download the PDF
    pdf.save(filename);
    
    return true;
  } catch (error) {
    console.error('Error generating PDF:', error);
    throw error;
  }
};

/**
 * Creates a DOM element for a user card that can be converted to PDF
 * @param {Object} user - User object
 * @returns {HTMLElement} - User card element
 */
const createUserCardElement = (user) => {
  const card = document.createElement('div');
  card.style.border = '1px solid #ddd';
  card.style.borderRadius = '8px';
  card.style.padding = '25px';
  card.style.backgroundColor = '#ffffff';
  card.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
  card.style.marginBottom = '10px';
  card.style.fontFamily = 'Arial, sans-serif';
  card.style.minHeight = '280px';

  // User info section
  const userInfo = document.createElement('div');
  userInfo.style.marginBottom = '15px';

  const createInfoRow = (label, value) => {
    const row = document.createElement('div');
    row.style.marginBottom = '8px';
    row.style.fontSize = '14px';
    row.style.color = '#333';
    const strong = document.createElement('strong');
    strong.textContent = label + ':';
    row.appendChild(strong);
    row.appendChild(document.createTextNode(' ' + (value || 'N/A')));
    return row;
  };

  userInfo.appendChild(createInfoRow('Employee ID', user.empId));
  userInfo.appendChild(createInfoRow('Department', user.department));
  userInfo.appendChild(createInfoRow('Location', user.location));

  if (user.firstName || user.secondName) {
    userInfo.appendChild(createInfoRow('Name', ((user.firstName || '') + ' ' + (user.secondName || '')).trim()));
  }

  card.appendChild(userInfo);

  // QR Code section
  const qrSection = document.createElement('div');
  qrSection.style.textAlign = 'center';
  qrSection.style.marginTop = '15px';

  if (user.qrCodeBase64) {
    const qrImg = document.createElement('img');
    qrImg.src = `data:image/png;base64,${user.qrCodeBase64}`;
    qrImg.style.width = '150px';
    qrImg.style.height = '150px';
    qrImg.style.border = '1px solid #ddd';
    qrImg.style.borderRadius = '4px';
    qrSection.appendChild(qrImg);
  } else {
    // Create a placeholder QR code
    const qrPlaceholder = document.createElement('div');
    qrPlaceholder.style.width = '150px';
    qrPlaceholder.style.height = '150px';
    qrPlaceholder.style.border = '1px solid #ddd';
    qrPlaceholder.style.borderRadius = '4px';
    qrPlaceholder.style.margin = '0 auto';
    qrPlaceholder.style.backgroundColor = '#f5f5f5';
    qrPlaceholder.style.display = 'flex';
    qrPlaceholder.style.alignItems = 'center';
    qrPlaceholder.style.justifyContent = 'center';
    qrPlaceholder.style.fontSize = '14px';
    qrPlaceholder.style.color = '#999';
    qrPlaceholder.textContent = 'QR Code';
    qrSection.appendChild(qrPlaceholder);
  }

  const qrLabel = document.createElement('div');
  qrLabel.textContent = 'QR Code';
  qrLabel.style.marginTop = '10px';
  qrLabel.style.fontSize = '14px';
  qrLabel.style.color = '#666';
  qrLabel.style.fontWeight = '500';
  qrSection.appendChild(qrLabel);

  card.appendChild(qrSection);

  return card;
};

